import { createContext, Dispatch, SetStateAction, useEffect, useState } from 'react';
import qs from 'qs';
import { ID, QueryResponseContextProps, QueryState } from './models';
import { Provider } from '../../../app/helpers/models';
import { load as cheerioLoad } from 'cheerio';

function createResponseContext<T>(initialState: QueryResponseContextProps<T>) {
  return createContext(initialState);
}

function isNotEmpty(obj: unknown) {
  return obj !== undefined && obj !== null && obj !== '';
}

function isNotEmptyObj(obj: Object) {
  return obj !== undefined && obj !== null && obj !== '' && Object.keys(obj).length > 0;
}

function isEmptyOrTooShort(obj: string | undefined, minLength: number) {
  return obj === undefined || obj === null || (obj && obj.length < minLength);
}

// Example: page=1&items_per_page=10&sort=id&order=desc&search=a&filter_name=a&filter_online=false
function stringifyRequestQuery(state: QueryState, options?: any, addConnection?: boolean, from?: string): string {
  // Force items_per_page to 50 and page 1 when addConnection; otherwise use state
  const enforcedState = addConnection ? { ...state, page: 1, items_per_page: 50 as const } : state;
  const pagination = qs.stringify(enforcedState, { filter: ['page', 'items_per_page'], skipNulls: true });
  const sort = qs.stringify(state, { filter: ['sort', 'order'], skipNulls: true });
  const search = addConnection
    ? qs.stringify({ q: state.search }, { skipNulls: true })
    : isNotEmpty(state.search)
    ? qs.stringify(state, { filter: ['search'], skipNulls: true })
    : '';
  const optionsFilter = options && isNotEmpty(options) ? qs.stringify(options, { skipNulls: true }) : ''; //options ? Object.keys(options).map((k: string) => `${k}=${options[k]}`) : undefined;
  const distanceFilter = from ? qs.stringify({ from: from }, { skipNulls: true }) : '';
  // Special handling for addConnection filters that the backend understands
  let addConnFilters = '';
  if (addConnection && state.filter) {
    const f: any = state.filter as any;
    const expertises = Array.isArray(f?.expertises)
      ? (f.expertises as any[]).map((e) => e?.uuid).filter((u) => !!u)
      : undefined;
    const zipcode = typeof f?.zipcode === 'string' ? f.zipcode : undefined;
    const city = typeof f?.city === 'string' ? f.city : undefined;
    const stateCodeOrName = typeof f?.state === 'string' ? f.state : undefined;
    const distance = typeof f?.distance === 'number' ? f.distance : undefined;
    const parts: string[] = [];
    if (expertises && expertises.length > 0) parts.push(`expertises=${expertises.join(',')}`);
    if (zipcode && zipcode.length === 5) parts.push(`zipcode=${zipcode}`);
    if (city) parts.push(`city=${encodeURIComponent(city)}`);
    if (stateCodeOrName) parts.push(`state=${encodeURIComponent(stateCodeOrName)}`);
    if (distance) parts.push(`distance=${distance}`);
    addConnFilters = parts.join('&');
  }

  // Only create filter_ parameters for non-addConnection mode to avoid duplication
  const filter = !addConnection && state.filter
    ? Object.entries(state.filter as Object)
        .filter((obj) => isNotEmpty(obj[1]))
        .map((obj) => {
          // Properly stringify objects to avoid [object object]
          const value = typeof obj[1] === 'object' ? JSON.stringify(obj[1]) : obj[1];
          return `filter_${obj[0]}=${encodeURIComponent(value)}`;
        })
        .join('&')
    : '';

  return [pagination, sort, search, filter, optionsFilter, distanceFilter, addConnFilters]
    .filter((f) => f)
    .join('&')
    .toLowerCase();
}

function parseRequestQuery(query: string): QueryState {
  const cache: unknown = qs.parse(query);
  return cache as QueryState;
}

function calculatedGroupingIsDisabled<T>(isLoading: boolean, data: Array<T> | undefined): boolean {
  if (isLoading) {
    return true;
  }

  return !data || !data.length;
}

function calculateIsAllDataSelected<T>(data: Array<T> | undefined, selected: Array<ID>): boolean {
  if (!data) {
    return false;
  }

  return data.length > 0 && data.length === selected.length;
}

function groupingOnSelect(id: ID, selected: Array<ID>, setSelected: Dispatch<SetStateAction<Array<ID>>>) {
  if (!id) {
    return;
  }

  if (selected.includes(id)) {
    setSelected(selected.filter((itemId) => itemId !== id));
  } else {
    const updatedSelected = [...selected];
    updatedSelected.push(id);
    setSelected(updatedSelected);
  }
}

function groupingOnSelectAll<T>(
  isAllSelected: boolean,
  setSelected: Dispatch<SetStateAction<Array<ID>>>,
  data?: Array<T & { id?: ID; uuid?: ID }>
) {
  if (isAllSelected) {
    setSelected([]);
    return;
  }

  if (!data || !data.length) {
    return;
  }

  // Handle both id and uuid properties
  const selectedIds = data
    .filter((item) => item.id || item.uuid)
    .map((item) => item.id || item.uuid);
  
  setSelected(selectedIds);
}

// Hook
function useDebounce(value: any | undefined, delay: number) {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);
      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler);
      };
    },
    [value, delay] // Only re-call effect if value or delay changes
  );
  return debouncedValue;
}

function calculateAge(birthDate: Date | string | undefined): number | undefined {
  if (!birthDate) {
    return undefined;
  }
  const today = new Date();
  const birth = new Date(birthDate);

  if (isNaN(birth.getTime())) {
    throw new Error('Invalid birth date');
  }

  let age = today.getFullYear() - birth.getFullYear();
  const monthDifference = today.getMonth() - birth.getMonth();
  const dayDifference = today.getDate() - birth.getDate();

  // Adjust age if the birthday hasn't occurred yet this year
  if (monthDifference < 0 || (monthDifference === 0 && dayDifference < 0)) {
    age--;
  }

  return age;
}

function isMultiProviderClinicAdmin(provider: Provider | null | undefined): boolean {
  return (provider?.is_admin && provider?.clinic?.providers && provider?.clinic?.providers?.length > 1) || false;
}

function extractTextFromHtml(html?: string) {
  if (!html) return '';
  return cheerioLoad(html).text().trim();
}

function isHtmlEmpty(html?: string) {
  return extractTextFromHtml(html).trim().length === 0;
}

export {
  createResponseContext,
  stringifyRequestQuery,
  parseRequestQuery,
  calculatedGroupingIsDisabled,
  calculateIsAllDataSelected,
  groupingOnSelect,
  groupingOnSelectAll,
  useDebounce,
  isNotEmpty,
  isNotEmptyObj,
  calculateAge,
  isMultiProviderClinicAdmin,
  extractTextFromHtml,
  isHtmlEmpty,
};
